// Cookie consent (UK GDPR / PECR): analytics — Google Analytics + Microsoft
// Clarity — load ONLY after an explicit opt-in, rejecting is exactly one
// equally-sized button, and the decision can be changed any time from the
// footer's "Cookie settings". Essential cookies (sign-in session, timezone,
// Stripe's fraud-prevention, reCAPTCHA security) don't need consent and are
// disclosed in the privacy policy instead.
//
// Persistence (the "why does every site keep asking me" fix): the decision is
// written three ways — a client cookie, localStorage, AND a server-set cookie
// via POST /api/consent. Safari's ITP caps SCRIPT-written cookies at 7 days,
// which is exactly why so many banners come back; an HTTP-set cookie keeps the
// full year. On load, any surviving copy restores the others.
//
// Bump CONSENT_VERSION to re-ask everyone (e.g. a new analytics vendor).
import {computed, watch} from 'vue';
import * as Sentry from '@sentry/nuxt';
import {allowSentryAnalytics, withdrawSentryAnalytics} from '~/utils/sentryConsent';
import {analyticsFreeRoute, cookieBannerFreeRoute} from '~/utils/analyticsFreeRoutes';

export const CONSENT_COOKIE = 'tl-cookie-consent';
export const CONSENT_VERSION = 1;

const COOKIE_OPTIONS = {
    maxAge: 60 * 60 * 24 * 365,
    sameSite: 'lax',
    path: '/',
};

export function useCookieConsent() {
    const cookie = useCookie(CONSENT_COOKIE, COOKIE_OPTIONS);
    const config = useRuntimeConfig();
    const route = useRoute();

    // Reactive shared state so every component (banner, footer dialog) agrees.
    const settingsOpen = useState('cookieConsentSettingsOpen', () => false);

    // Captured during setup — loadAnalytics runs later from event handlers,
    // where composables that need the Nuxt instance would throw.
    let gtagInitialize = null;
    if (import.meta.client) {
        try {
            gtagInitialize = useGtag().initialize;
        } catch { /* gtag module absent in this build */
        }
    }
    const analyticsLoaded = useState('cookieConsentAnalyticsLoaded', () => false);

    // Widgets embedded on other sites and clubhouse TV displays never show
    // the banner and never load analytics. See cookieBannerFreeRoute.
    const isEmbed = computed(() => cookieBannerFreeRoute(route.path));
    // Pages that never load analytics or session recording (the embeds, and the
    // email preferences page whose address carries a token). See analyticsFreeRoutes.
    const analyticsFree = computed(() => analyticsFreeRoute(route.path));

    const decision = computed(() => {
        const value = cookie.value;
        if (!value || typeof value !== 'object') return null;
        if (value.v !== CONSENT_VERSION) return null; // policy changed — re-ask
        return value;
    });

    const needsDecision = computed(() => !isEmbed.value && decision.value == null);
    const analyticsAllowed = computed(() => decision.value?.analytics === true);

    // ── restore: cookie gone (ITP eviction, cleared site data) but localStorage
    //    survived → honour the old decision silently instead of re-asking ──
    const restoreFromLocalStorage = () => {
        if (import.meta.server || cookie.value) return;
        try {
            const raw = window.localStorage.getItem(CONSENT_COOKIE);
            if (!raw) return;
            const stored = JSON.parse(raw);
            if (stored?.v === CONSENT_VERSION) {
                cookie.value = stored;
                persistServerSide(stored);
            }
        } catch {
            /* corrupt copy — the banner will just ask again */
        }
    };

    // HTTP-set cookie (server route) — immune to Safari's 7-day script-cookie cap.
    const persistServerSide = (value) => {
        $fetch('/api/consent', {method: 'POST', body: value}).catch(() => {
            /* best-effort: the client cookie + localStorage still stand */
        });
    };

    const decide = (analytics) => {
        const value = {v: CONSENT_VERSION, analytics, at: new Date().toISOString()};
        const hadAnalytics = analyticsLoaded.value;
        cookie.value = value;
        try {
            window.localStorage.setItem(CONSENT_COOKIE, JSON.stringify(value));
        } catch { /* private mode — cookie still stands */
        }
        persistServerSide(value);
        settingsOpen.value = false;

        if (analytics) {
            loadAnalytics();
        } else if (hadAnalytics) {
            // Withdrawal: stop Sentry Replay and tracing, purge the vendors'
            // cookies and reload so no tracker keeps running in this session.
            withdrawSentryAnalytics(Sentry);
            purgeAnalyticsCookies();
            window.location.reload();
        }
    };

    // ── vendor loading (only ever called after an opt-in) ──
    const loadAnalytics = () => {
        if (import.meta.server || analyticsLoaded.value || analyticsFree.value) return;
        analyticsLoaded.value = true;

        // Google Analytics — nuxt-gtag is configured with initMode: 'manual'.
        gtagInitialize?.();

        // Sentry Session Replay and tracing (errors are captured regardless).
        allowSentryAnalytics(Sentry);

        // Microsoft Clarity — the standard snippet, id per environment.
        const clarityId = config.public.clarityId;
        if (clarityId && !window.clarity) {
            /* eslint-disable */
            (function (c, l, a, r, i, t, y) {
                c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
                t = l.createElement(r);
                t.async = 1;
                t.src = 'https://www.clarity.ms/tag/' + i;
                y = l.getElementsByTagName(r)[0];
                y.parentNode.insertBefore(t, y);
            })(window, document, 'clarity', 'script', clarityId);
            /* eslint-enable */
        }
    };

    // Best-effort removal of the vendors' cookies on withdrawal — every name
    // GA4 and Clarity are known to set, across the domain variants they use.
    const purgeAnalyticsCookies = () => {
        const names = document.cookie.split(';').map(c => c.split('=')[0].trim());
        const analyticsNames = names.filter(name =>
            name === '_ga' || name.startsWith('_ga_') ||
            name === '_gid' || name === '_gat' ||
            name === '_clck' || name === '_clsk' ||
            name === 'CLID' || name === 'MUID' || name === 'ANONCHK' || name === 'SM');
        const host = window.location.hostname;
        const domains = ['', host, `.${host}`, `.${host.split('.').slice(-2).join('.')}`];
        for (const name of analyticsNames) {
            for (const domain of domains) {
                document.cookie = `${name}=; Max-Age=0; path=/` + (domain ? `; domain=${domain}` : '');
            }
        }
    };

    // Boot: restore an evicted decision, then honour it.
    const applyOnLoad = () => {
        restoreFromLocalStorage();
        if (analyticsAllowed.value) loadAnalytics();
        // A later restore (e.g. hydration timing) still gets applied.
        watch(analyticsAllowed, (allowed) => {
            if (allowed) loadAnalytics();
        });
    };

    return {
        needsDecision,
        analyticsAllowed,
        settingsOpen,
        acceptAnalytics: () => decide(true),
        essentialOnly: () => decide(false),
        decide,
        applyOnLoad,
        openSettings: () => (settingsOpen.value = true),
    };
}
